import React, { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { PriorityBadge, HealthBadge, StatusBadge, ReviewBadge, fmtDate, initials } from "../lib/ui";
import {
  RefreshCw, Lock, Unlock, Ban, UserPlus, Paperclip, CheckCircle2, Upload, Download, GitFork, Plus, Pencil, Trash2, Sparkles, Loader2,
  Star, EyeOff, Eye, FolderInput, Bell, Share2, X,
} from "lucide-react";

const TABS = ["Overview", "Dependencies", "Escalations", "Updates", "Comments", "Attachments", "History", "Audit"];

export default function TaskDrawer({ taskId, onClose, onChanged }) {
  const { users, departments, projects, projName, userName, deptName, favourites, toggleFavourite } = useData();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [modal, setModal] = useState(null);
  const isAdmin = ["master_admin", "admin"].includes(user?.role);
  const canManage = ["master_admin", "admin", "manager"].includes(user?.role);
  const canEdit = user?.role !== "viewer";
  const isFav = !!favourites?.task?.has(taskId);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load task");
      onClose();
    }
  }, [taskId, onClose]);

  useEffect(() => { load(); }, [load]);

  const afterAction = async () => { await load(); onChanged?.(); setModal(null); };

  if (!task) return null;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl p-0 overflow-y-auto" data-testid="task-drawer">
        {/* Header */}
        <div className="p-5 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{task.human_id}</span>
            {task.locked && <span className="text-xs text-amber-600 flex items-center gap-1"><Lock size={12} /> Locked</span>}
            {task.editing_by && task.editing_by !== user?.full_name && (
              <span className="text-xs text-orange-600">Currently being edited by {task.editing_by}</span>
            )}
          </div>
          <h2 className="text-lg font-bold text-slate-900 mt-1">{task.title}</h2>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <StatusBadge value={task.execution_status} />
            <HealthBadge value={task.health} />
            <PriorityBadge value={task.priority} />
            <ReviewBadge value={task.review_status} />
            {task.is_overdue && <span className="text-xs text-red-600 font-medium">{task.days_overdue}d overdue</span>}
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>Progress</span><span>{task.progress}%</span>
            </div>
            <Progress value={task.progress} className="h-2" />
          </div>
          {/* action buttons */}
          <div className="flex gap-1.5 mt-4 flex-wrap">
            {canEdit && <ActBtn icon={Pencil} label="Edit" onClick={() => setModal("edit")} testid="act-edit" />}
            <ActBtn icon={Sparkles} label="AI Summary" onClick={() => setModal("ai-summary")} testid="act-ai-summary" />
            <ActBtn icon={RefreshCw} label="Update" onClick={() => setModal("update")} testid="act-update" />
            <ActBtn icon={CheckCircle2} label="Status" onClick={() => setModal("status")} testid="act-status" />
            <ActBtn icon={UserPlus} label="Reassign" onClick={() => setModal("reassign")} testid="act-reassign" />
            <ActBtn icon={Ban} label="Block" onClick={() => setModal("block")} testid="act-block" />
            <ActBtn icon={GitFork} label="Dependency" onClick={() => setModal("dep")} testid="act-dependency" />
            <ActBtn icon={CheckCircle2} label="Complete" onClick={() => setModal("complete")} testid="act-complete" />
            <ActBtn icon={Star} label={isFav ? "Unfavourite" : "Favourite"} active={isFav} testid="act-favourite"
              onClick={() => toggleFavourite("task", task.id)} />
            <ActBtn icon={Bell} label="Reminders" onClick={() => setModal("reminders")} testid="act-reminders" />
            {canManage && <ActBtn icon={Share2} label="Share" onClick={() => setModal("share")} testid="act-share" />}
            {canManage && <ActBtn icon={FolderInput} label="Move" onClick={() => setModal("move")} testid="act-move" />}
            {canManage && (task.is_hidden
              ? <ActBtn icon={Eye} label="Unhide" testid="act-unhide" onClick={async () => {
                  await api.post(`/tasks/${task.id}/unhide`); toast.success("Task unhidden"); load(); onChanged?.();
                }} />
              : <ActBtn icon={EyeOff} label="Hide" testid="act-hide" onClick={async () => {
                  await api.post(`/tasks/${task.id}/hide`); toast.success("Task hidden"); load(); onChanged?.();
                }} />)}
            {isAdmin && (task.locked
              ? <ActBtn icon={Unlock} label="Unlock" onClick={async () => { await api.post(`/tasks/${task.id}/unlock`); load(); }} />
              : <ActBtn icon={Lock} label="Lock" onClick={async () => { await api.post(`/tasks/${task.id}/lock`); load(); }} />)}
            {isAdmin && <ActBtn icon={Trash2} label="Delete" testid="act-delete" onClick={async () => {
              if (!window.confirm(`Delete ${task.human_id} "${task.title}"? This archives the task.`)) return;
              await api.delete(`/tasks/${task.id}`);
              toast.success(`${task.human_id} deleted`);
              onChanged?.(); onClose();
            }} />}
          </div>
        </div>

        {/* Next action panel */}
        <div className="p-5 bg-blue-50/60 border-b" data-testid="task-next-action-panel">
          <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-2">What needs to happen next?</div>
          <div className="text-sm text-slate-800">{task.next_action || "No next action set."}</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <OwnerCard label="Current Action Owner" name={userName(task.current_action_owner_id)} accent testid="task-next-action-owner-card" />
            <InfoCard label="Current Action Dept" value={deptName(task.current_action_department_id)} />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="px-5 pt-3">
          <TabsList className="flex-wrap h-auto justify-start">
            {TABS.map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`task-detail-tab-${t.toLowerCase()}`}>{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="Overview"><Overview task={task} userName={userName} deptName={deptName} projName={projName} /></TabsContent>
          <TabsContent value="Dependencies"><DepsTab task={task} /></TabsContent>
          <TabsContent value="Escalations"><EscalationsTab task={task} /></TabsContent>
          <TabsContent value="Updates"><UpdatesTab task={task} /></TabsContent>
          <TabsContent value="Comments"><CommentsTab task={task} users={users} me={user} /></TabsContent>
          <TabsContent value="Attachments"><AttachmentsTab task={task} onChange={load} /></TabsContent>
          <TabsContent value="History"><HistoryTab task={task} /></TabsContent>
          <TabsContent value="Audit"><AuditTab task={task} isAdmin={isAdmin} /></TabsContent>
        </Tabs>
        <div className="h-8" />

        {modal === "update" && <UpdateModal task={task} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "edit" && <EditTaskModal task={task} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "ai-summary" && <AISummaryModal task={task} onClose={() => setModal(null)} />}
        {modal === "status" && <StatusModal task={task} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "reassign" && <ReassignModal task={task} users={users} departments={departments} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "block" && <BlockModal task={task} departments={departments} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "dep" && <DepModal task={task} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "complete" && <CompleteModal task={task} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "move" && <MoveModal task={task} projects={projects} projName={projName} onClose={() => setModal(null)} onDone={afterAction} />}
        {modal === "reminders" && <RemindersModal task={task} users={users} onClose={() => setModal(null)} />}
        {modal === "share" && <ShareTaskModal task={task} users={users} onClose={() => setModal(null)} />}
      </SheetContent>
    </Sheet>
  );
}

const ActBtn = ({ icon: Icon, label, onClick, testid, active }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border hover:bg-slate-50 ${active ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white text-slate-700"}`}>
    <Icon size={13} className={active ? "fill-amber-500 text-amber-500" : ""} /> {label}
  </button>
);

const OwnerCard = ({ label, name, accent, testid }) => (
  <div data-testid={testid} className={`rounded-lg p-2.5 border ${accent ? "bg-white border-blue-200" : "bg-slate-50"}`}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="flex items-center gap-1.5 mt-1">
      <div className="h-5 w-5 rounded-full bg-blue-600 text-white text-[9px] flex items-center justify-center">{initials(name)}</div>
      <span className="text-sm font-medium text-slate-800">{name}</span>
    </div>
  </div>
);
const InfoCard = ({ label, value }) => (
  <div className="rounded-lg p-2.5 border bg-slate-50">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-medium text-slate-800 mt-1">{value}</div>
  </div>
);

function Overview({ task, userName, deptName, projName }) {
  const rows = [
    ["Primary Owner", userName(task.primary_owner_id)],
    ["Accountable Owner", userName(task.accountable_owner_id)],
    ["Department", deptName(task.department_id)],
    ["Project", task.project_id ? projName(task.project_id) : "—"],
    ["Reviewer", userName(task.reviewer_id)],
    ["Approver", userName(task.approver_id)],
    ["Original Due", fmtDate(task.original_due_date)],
    ["Current Due", fmtDate(task.current_due_date)],
    ["Commitment Date", fmtDate(task.commitment_date)],
    ["Actual Completion", fmtDate(task.actual_completion_date)],
    ["Estimated Hours", task.estimated_hours ?? "—"],
    ["Due-date changes", task.due_date_change_count || 0],
    ["Commitment changes", task.commitment_change_count || 0],
    ["Version", task.version],
  ];
  return (
    <div className="py-4 space-y-4">
      {task.description && <p className="text-sm text-slate-700">{task.description}</p>}
      {task.commitment_change_count >= 2 && (
        <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-md px-3 py-2">
          Commitment changed {task.commitment_change_count} times
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">{k}</span>
            <span className="text-sm text-slate-800">{v}</span>
          </div>
        ))}
      </div>
      {task.tags?.length > 0 && (
        <div className="flex gap-1.5 flex-wrap pt-2">
          {task.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">#{t}</span>)}
        </div>
      )}
    </div>
  );
}

function DepsTab({ task }) {
  const [d, setD] = useState({ predecessors: [], successors: [] });
  useEffect(() => { api.get(`/tasks/${task.id}/dependencies`).then((r) => setD(r.data)); }, [task.id]);
  const Row = ({ items, label }) => (
    <div>
      <div className="text-xs font-semibold text-slate-500 mb-1.5">{label}</div>
      {items.length === 0 ? <div className="text-xs text-slate-400">None</div> :
        items.map((x) => (
          <div key={x.id} className="text-sm border rounded-md px-3 py-2 mb-1.5 flex items-center gap-2">
            <GitFork size={13} className="text-slate-400" />
            <span className="font-mono text-xs">{x.dep_type}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${x.hard ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>{x.hard ? "Hard" : "Soft"}</span>
            {x.impact && <span className="text-xs text-slate-500 ml-auto">{x.impact}</span>}
          </div>
        ))}
    </div>
  );
  return <div className="py-4 space-y-4"><Row items={d.predecessors} label="Depends on (predecessors)" /><Row items={d.successors} label="Blocks (successors)" /></div>;
}

function EscalationsTab({ task }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/tasks/${task.id}/escalations`).then((r) => setItems(r.data)).catch(() => {}); }, [task.id]);
  const LEVEL = { 1: "#2563EB", 2: "#CA8A04", 3: "#EA580C", 4: "#DC2626", 5: "#991B1B" };
  if (items.length === 0) return <div className="py-6 text-sm text-slate-400 text-center">No escalations on this task.</div>;
  return (
    <div className="py-4 space-y-2">
      {items.map((e) => (
        <div key={e.id} className="border rounded-md px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-slate-400">{e.human_id}</span>
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: LEVEL[e.level] || "#2563EB" }}>L{e.level}</span>
            <span className="text-xs text-slate-500">{e.status}</span>
          </div>
          <div className="text-sm mt-0.5">{e.rule_name}</div>
          <div className="text-xs text-slate-500">{e.trigger_reason} · {fmtDate(e.triggered_at)}</div>
        </div>
      ))}
    </div>
  );
}

function UpdatesTab({ task }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/tasks/${task.id}/updates`).then((r) => setItems(r.data)); }, [task.id]);
  if (items.length === 0) return <div className="py-6 text-sm text-slate-400 text-center">No updates yet. Use the Update button above.</div>;
  return (
    <div className="py-4 space-y-3">
      {items.map((u) => (
        <div key={u.id} className="border-l-2 border-blue-300 pl-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{u.updated_by_name}</span>
            <span>{fmtDate(u.created_at)}</span>
            {u.progress != null && <span className="bg-blue-50 text-blue-700 rounded px-1.5">{u.progress}%</span>}
          </div>
          {u.work_completed && <div className="text-sm mt-0.5">{u.work_completed}</div>}
          {u.comment && <div className="text-sm text-slate-600">{u.comment}</div>}
          {u.next_action && <div className="text-xs text-slate-500 mt-0.5">Next: {u.next_action}</div>}
        </div>
      ))}
    </div>
  );
}

function CommentsTab({ task, users, me }) {
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const load = () => api.get(`/tasks/${task.id}/comments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [task.id]);
  const add = async () => {
    if (!body.trim()) return;
    const mentions = users.filter((u) => body.includes("@" + u.full_name.split(" ")[0])).map((u) => u.id);
    await api.post(`/tasks/${task.id}/comments`, { body, mentions });
    setBody(""); load();
  };
  return (
    <div className="py-4 space-y-3">
      {items.map((c) => (
        <div key={c.id} className="flex gap-2">
          <div className="h-7 w-7 rounded-full bg-slate-700 text-white text-[10px] flex items-center justify-center shrink-0">{initials(c.author_name)}</div>
          <div className="bg-slate-50 rounded-lg px-3 py-2 flex-1">
            <div className="text-xs text-slate-500"><span className="font-medium text-slate-700">{c.author_name}</span> · {fmtDate(c.created_at)}</div>
            <div className="text-sm mt-0.5">{c.body}</div>
          </div>
        </div>
      ))}
      <div className="flex gap-2 pt-2">
        <Input data-testid="comment-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment… use @Name to mention" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button data-testid="comment-submit" onClick={add}>Post</Button>
      </div>
    </div>
  );
}

function AttachmentsTab({ task, onChange }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get(`/tasks/${task.id}/attachments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [task.id]);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tasks/${task.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const dl = async (a) => {
    const res = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url; link.download = a.original_filename; link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="py-4 space-y-2">
      <label className="flex items-center gap-2 text-sm border-2 border-dashed rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-50 text-slate-600" data-testid="attachment-upload">
        <Upload size={16} /> {uploading ? "Uploading…" : "Upload file (PDF, Excel, Word, image, ZIP)"}
        <input type="file" className="hidden" onChange={upload} />
      </label>
      {items.map((a) => (
        <div key={a.id} className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm">
          <Paperclip size={14} className="text-slate-400" />
          <span className="flex-1 truncate">{a.original_filename}</span>
          <span className="text-xs text-slate-400">v{a.version} · {(a.size / 1024).toFixed(0)} KB</span>
          <button onClick={() => dl(a)} className="text-blue-600"><Download size={15} /></button>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ task }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/tasks/${task.id}/history`).then((r) => setItems(r.data)); }, [task.id]);
  return (
    <div className="py-4 space-y-2">
      {items.map((h) => (
        <div key={h.id} className="text-sm flex items-center gap-2">
          <span className="text-xs text-slate-400 w-24 shrink-0">{fmtDate(h.changed_at)}</span>
          <span className="capitalize font-medium text-slate-700">{h.field.replace("_", " ")}</span>
          <span className="text-slate-500">{h.old_value || "—"} → {h.new_value || "—"}</span>
          <span className="text-xs text-slate-400 ml-auto">{h.changed_by}</span>
        </div>
      ))}
    </div>
  );
}

function AuditTab({ task, isAdmin }) {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/tasks/${task.id}/audit`).then((r) => setItems(r.data)).catch(() => setErr(true)); }, [task.id]);
  if (err) return <div className="py-6 text-sm text-slate-400 text-center">Audit log requires elevated permission.</div>;
  return (
    <div className="py-4 space-y-2">
      {items.map((a) => (
        <div key={a.id} className="text-sm flex items-center gap-2">
          <span className="text-xs text-slate-400 w-24 shrink-0">{fmtDate(a.timestamp)}</span>
          <span className="capitalize font-medium text-slate-700">{a.action.replace("_", " ")}</span>
          <span className="text-xs text-slate-400 ml-auto">{a.user_name}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Action modals ----
function ModalShell({ title, children, onClose, onSubmit, submitLabel = "Save", testid }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="modal-submit">{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateModal({ task, onClose, onDone }) {
  const [f, setF] = useState({ work_completed: "", comment: "", next_action: task.next_action || "", progress: task.progress, commitment_date: "" });
  const submit = async () => {
    await api.post(`/tasks/${task.id}/updates`, { ...f, progress: Number(f.progress), commitment_date: f.commitment_date || null });
    toast.success("Update added"); onDone();
  };
  return (
    <ModalShell title="Add Work Update" onClose={onClose} onSubmit={submit} testid="update-modal">
      <div><Label>Work completed</Label><Textarea value={f.work_completed} onChange={(e) => setF({ ...f, work_completed: e.target.value })} /></div>
      <div><Label>Comment</Label><Textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} /></div>
      <div><Label>Next action</Label><Input value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Progress %</Label>
          <Select value={String(f.progress)} onValueChange={(v) => setF({ ...f, progress: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{[0,10,25,50,75,90,100].map((p) => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>New commitment date</Label><Input type="date" value={f.commitment_date} onChange={(e) => setF({ ...f, commitment_date: e.target.value })} /></div>
      </div>
    </ModalShell>
  );
}

function StatusModal({ task, onClose, onDone }) {
  const [status, setStatus] = useState(task.execution_status);
  const submit = async () => { await api.post(`/tasks/${task.id}/status`, { execution_status: status }); toast.success("Status changed"); onDone(); };
  return (
    <ModalShell title="Change Status" onClose={onClose} onSubmit={submit} testid="status-modal">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
        <SelectContent>{["Draft","Not Started","Open","In Progress","Waiting","On Hold","Completed","Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    </ModalShell>
  );
}

function AISummaryModal({ task, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.post(`/ai/task-summary/${task.id}`).then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "AI unavailable"));
  }, [task.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="ai-summary-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-blue-600" /> AI Task Summary — {task.human_id}</DialogTitle></DialogHeader>
        {!data && !err && <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={18} />Analysing task…</div>}
        {err && <div className="py-6 text-sm text-slate-500">{err}</div>}
        {data?.ai ? (
          <div className="space-y-2 text-sm">
            {Object.entries(data.ai).map(([k, v]) => (
              <div key={k}><div className="text-xs font-semibold text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
                {Array.isArray(v) ? <ul className="list-disc pl-5 text-slate-700">{v.map((x, i) => <li key={i}>{typeof x === "string" ? x : (x.text || JSON.stringify(x))}</li>)}</ul> : <div className="text-slate-700">{String(v || "—")}</div>}</div>
            ))}
          </div>
        ) : data && !data.ai ? <div className="py-6 text-sm text-slate-500">{data.reason || "AI not available."}</div> : null}
      </DialogContent>
    </Dialog>
  );
}

function EditTaskModal({ task, onClose, onDone }) {  const [f, setF] = useState({
    title: task.title || "", description: task.description || "",
    priority: task.priority || "Medium", current_due_date: task.current_due_date || "",
  });
  const submit = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    await api.put(`/tasks/${task.id}`, { ...f, current_due_date: f.current_due_date || null });
    toast.success("Task updated");
    onDone();
  };
  return (
    <ModalShell title="Edit Task" onClose={onClose} onSubmit={submit} testid="edit-task-modal">
      <div><Label>Title *</Label><Input data-testid="edit-task-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
      <div><Label>Description</Label><Textarea data-testid="edit-task-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Priority</Label>
          <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
            <SelectTrigger data-testid="edit-task-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{["Low", "Medium", "High", "Critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Due date</Label><Input type="date" data-testid="edit-task-due" value={f.current_due_date} onChange={(e) => setF({ ...f, current_due_date: e.target.value })} /></div>
      </div>
    </ModalShell>
  );
}

function ReassignModal({ task, users, departments, onClose, onDone }) {
  const [f, setF] = useState({ current_action_owner_id: task.current_action_owner_id || "", current_action_department_id: task.current_action_department_id || "" });
  const submit = async () => { await api.post(`/tasks/${task.id}/reassign`, f); toast.success("Reassigned"); onDone(); };
  return (
    <ModalShell title="Reassign Current Action" onClose={onClose} onSubmit={submit} testid="reassign-modal">
      <div><Label>Current Action Owner</Label>
        <Select value={f.current_action_owner_id} onValueChange={(v) => setF({ ...f, current_action_owner_id: v })}>
          <SelectTrigger data-testid="reassign-owner"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Current Action Department</Label>
        <Select value={f.current_action_department_id} onValueChange={(v) => setF({ ...f, current_action_department_id: v })}>
          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </ModalShell>
  );
}

function BlockModal({ task, departments, onClose, onDone }) {
  const [f, setF] = useState({ category: "Approval", description: "", expected_resolution_date: "", escalation_required: false });
  const submit = async () => {
    if (!f.description) return toast.error("Description required");
    await api.post(`/tasks/${task.id}/blockers`, f); toast.success("Task marked blocked"); onDone();
  };
  return (
    <ModalShell title="Mark Blocked" onClose={onClose} onSubmit={submit} submitLabel="Mark Blocked" testid="block-modal">
      <div><Label>Blocker Category</Label>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{["Person","Department","External Party","Management Decision","Document","Approval","Payment","Information","Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
      <div><Label>Expected resolution date</Label><Input type="date" value={f.expected_resolution_date} onChange={(e) => setF({ ...f, expected_resolution_date: e.target.value })} /></div>
    </ModalShell>
  );
}

function DepModal({ task, onClose, onDone }) {
  const [tasks, setTasks] = useState([]);
  const [f, setF] = useState({ predecessor_id: "", dep_type: "Finish-to-Start", hard: true, impact: "" });
  useEffect(() => { api.get("/tasks", { params: { page_size: 200 } }).then((r) => setTasks(r.data.items.filter((t) => t.id !== task.id))); }, [task.id]);
  const submit = async () => {
    if (!f.predecessor_id) return toast.error("Select a predecessor task");
    try {
      await api.post("/dependencies", { ...f, successor_id: task.id });
      toast.success("Dependency added"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <ModalShell title="Add Dependency" onClose={onClose} onSubmit={submit} testid="dep-modal">
      <div><Label>This task depends on (predecessor)</Label>
        <Select value={f.predecessor_id} onValueChange={(v) => setF({ ...f, predecessor_id: v })}>
          <SelectTrigger data-testid="dep-predecessor"><SelectValue placeholder="Select task" /></SelectTrigger>
          <SelectContent>{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.human_id} — {t.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Type</Label>
        <Select value={f.dep_type} onValueChange={(v) => setF({ ...f, dep_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{["Finish-to-Start","Start-to-Start","Finish-to-Finish","Start-to-Finish"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Impact</Label><Input value={f.impact} onChange={(e) => setF({ ...f, impact: e.target.value })} /></div>
    </ModalShell>
  );
}

function MoveModal({ task, projects, projName, onClose, onDone }) {
  const [pid, setPid] = useState(task.project_id || "none");
  const submit = async () => {
    try {
      await api.post(`/tasks/${task.id}/move`, { project_id: pid === "none" ? null : pid });
      toast.success("Task moved");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not move task"); }
  };
  return (
    <ModalShell title="Move Task / Change Project" onClose={onClose} onSubmit={submit} submitLabel="Move Task" testid="move-modal">
      <p className="text-sm text-slate-600">
        Move <span className="font-mono">{task.human_id}</span> from <b>{task.project_id ? projName(task.project_id) : "No project"}</b> to:
      </p>
      <div><Label>Destination project</Label>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger data-testid="move-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— No project —</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-slate-400">Task ID, owners, status, comments, attachments, dependencies and history are all preserved.</p>
    </ModalShell>
  );
}

const REMINDER_PRESETS = [
  { label: "At due time", v: 0 }, { label: "15 minutes before", v: 15 },
  { label: "30 minutes before", v: 30 }, { label: "1 hour before", v: 60 },
  { label: "3 hours before", v: 180 }, { label: "1 day before", v: 1440 },
  { label: "2 days before", v: 2880 }, { label: "3 days before", v: 4320 },
  { label: "1 week before", v: 10080 },
];

function RemindersModal({ task, users, onClose }) {
  const [list, setList] = useState([]);
  const uname = (id) => users.find((u) => u.id === id)?.full_name || "—";
  const [preset, setPreset] = useState("1440");
  const [customAt, setCustomAt] = useState("");
  const [recipient, setRecipient] = useState("primary");
  const [recipientUser, setRecipientUser] = useState("");
  const load = () => api.get(`/tasks/${task.id}/reminders`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    const body = { recipient };
    if (recipient === "user") {
      if (!recipientUser) return toast.error("Select a user");
      body.recipient_user_id = recipientUser;
    }
    if (preset === "custom") {
      if (!customAt) return toast.error("Pick a date & time");
      body.reminder_at = customAt;
      body.label = "Custom";
    } else {
      body.offset_minutes = Number(preset);
      body.label = REMINDER_PRESETS.find((p) => String(p.v) === preset)?.label || "Reminder";
    }
    try {
      await api.post(`/tasks/${task.id}/reminders`, body);
      toast.success("Reminder added"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add reminder"); }
  };
  const del = async (rid) => { await api.delete(`/tasks/${task.id}/reminders/${rid}`); toast.success("Reminder removed"); load(); };
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="reminders-modal">
        <DialogHeader><DialogTitle>Task Reminders</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            {list.length === 0 && <div className="text-sm text-slate-400">No reminders yet.</div>}
            {list.map((r) => (
              <div key={r.id} data-testid={`reminder-row-${r.id}`} className="flex items-center justify-between text-sm border rounded-md px-3 py-2 bg-slate-50">
                <div>
                  <div className="font-medium text-slate-800">{r.label || "Reminder"} · <span className={r.status === "sent" ? "text-emerald-600" : "text-blue-600"}>{r.status}</span></div>
                  <div className="text-xs text-slate-500">{fmt(r.reminder_at)} → {uname(r.recipient_user_id)}</div>
                </div>
                <button data-testid={`reminder-delete-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <Label>Add reminder</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="reminder-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REMINDER_PRESETS.map((p) => <SelectItem key={p.v} value={String(p.v)}>{p.label}</SelectItem>)}
                <SelectItem value="custom">Custom date/time…</SelectItem>
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <Input type="datetime-local" data-testid="reminder-custom" value={customAt} onChange={(e) => setCustomAt(e.target.value)} />
            )}
            <Select value={recipient} onValueChange={setRecipient}>
              <SelectTrigger data-testid="reminder-recipient"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="primary">Primary Owner</SelectItem>
                <SelectItem value="action">Current Action Owner</SelectItem>
                <SelectItem value="accountable">Accountable Owner</SelectItem>
                <SelectItem value="user">Specific user…</SelectItem>
              </SelectContent>
            </Select>
            {recipient === "user" && (
              <Select value={recipientUser} onValueChange={setRecipientUser}>
                <SelectTrigger data-testid="reminder-user"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <p className="text-xs text-slate-400">Presets are relative to the task due date (fires as an in-app notification). Times are UTC.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={add} data-testid="reminder-add">Add Reminder</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteModal({ task, onClose, onDone }) {
  const submit = async () => { await api.post(`/tasks/${task.id}/complete`); toast.success("Task completed"); onDone(); };
  return (
    <ModalShell title="Complete Task" onClose={onClose} onSubmit={submit} submitLabel="Confirm Complete" testid="complete-modal">
      <p className="text-sm text-slate-600">
        {task.workflow_type === "approval" ? "This task requires approval. It will move to Pending Approval."
          : task.workflow_type === "controlled" ? "This task requires review. It will move to Pending Review."
          : "This will mark the task as Completed."}
      </p>
    </ModalShell>
  );
}


function ShareTaskModal({ task, users, onClose }) {
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState({ shared_with_user_id: "", permission: "view", expires_at: "" });
  const load = useCallback(() => api.get(`/shares?entity_type=task&entity_id=${task.id}`).then((r) => setShares(r.data)).catch(() => {}), [task.id]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.shared_with_user_id) return toast.error("Select a user");
    try {
      await api.post("/shares", { entity_type: "task", entity_id: task.id, ...form, expires_at: form.expires_at || null });
      toast.success("Task shared"); setForm({ shared_with_user_id: "", permission: "view", expires_at: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
  };
  const revoke = async (id) => { await api.post(`/shares/${id}/revoke`); toast.success("Revoked"); load(); };
  const changePerm = async (s, permission) => { await api.put(`/shares/${s.id}`, { permission, expires_at: s.expires_at || null }); load(); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="task-share-modal">
        <DialogHeader><DialogTitle>Share: {task.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Give a teammate direct access to this task even if it's outside their team.</p>
          <Select value={form.shared_with_user_id} onValueChange={(v) => setForm({ ...form, shared_with_user_id: v })}>
            <SelectTrigger data-testid="task-share-user"><SelectValue placeholder="Select user…" /></SelectTrigger>
            <SelectContent>{users.filter((u) => u.id !== task.primary_owner_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select value={form.permission} onValueChange={(v) => setForm({ ...form, permission: v })}>
              <SelectTrigger className="flex-1" data-testid="task-share-perm"><SelectValue /></SelectTrigger>
              <SelectContent>{["view", "edit", "manage"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="datetime-local" className="flex-1" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="task-share-expiry" />
          </div>
          <Button className="w-full" onClick={submit} data-testid="task-share-submit"><Share2 size={14} className="mr-1.5" />Share</Button>
          <div className="space-y-2 pt-1">
            <div className="text-xs font-semibold text-slate-500">Current Shares ({shares.length})</div>
            {shares.length === 0 && <div className="text-sm text-slate-400">Not shared yet.</div>}
            {shares.map((s) => (
              <div key={s.id} data-testid={`task-share-row-${s.id}`} className="flex items-center gap-2 text-sm border rounded-lg p-2">
                <div className="min-w-0 flex-1"><div className="truncate text-slate-700">{s.shared_with_name}</div><div className="text-[11px] text-slate-400">{s.shared_with_email} · <span className={s.status === "active" ? "text-emerald-600" : "text-red-500"}>{s.status}</span></div></div>
                {s.status === "active" && (
                  <Select value={s.permission} onValueChange={(v) => changePerm(s, v)}>
                    <SelectTrigger className="w-24 h-8" data-testid={`task-share-perm-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["view", "edit", "manage"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {s.status === "active" && <button onClick={() => revoke(s.id)} data-testid={`task-share-revoke-${s.id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><X size={14} /></button>}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
